/**
 * tenants.js (r7.6) — API wrappers for the tenant surfaces. One function per gateway route; permissions are
 * enforced server-side, the pages only decide what to render.
 *
 *   public     GET  /api/tenants/current                       the tenant this host / build belongs to
 *   organizer  GET|PUT /api/organizer/tenant[?tenant=]         branding (owner) — commercial fields → 400
 *              GET  /api/organizer/audience[?tenant=]          consent stats; /export → CSV (audit-logged)
 *              GET  /api/app-updates/:tenant/manifest          OTA manifest (public, per tenant)
 *   admin      /api/admin/tenants[/:slug][/activate|suspend|organizers|members]
 *              GET  /api/admin/audience/pool[/export]          platform pool (audience.pool.*)
 *              /api/admin/custom-themes[/:id|/validate]        superadmin palette editor (theme.palette)
 */
import { api } from '@/lib/api';
import client from '@/api/client';

const qs = (o = {}) => {
  const p = new URLSearchParams();
  Object.entries(o).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') p.set(k, String(v)); });
  const s = p.toString();
  return s ? `?${s}` : '';
};

/** Download a CSV route through the axios client (auth header + refresh) and hand it to the browser. */
async function downloadCsv(path, filename) {
  const r = await client.get(path, { responseType: 'blob' });
  const blob = r.data instanceof Blob ? r.data : new Blob([r.data], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  return Number(r.headers?.['x-row-count'] || 0);
}

export const tenantPublic = {
  current: () => api.get('/api/tenants/current'),
  manifest: (slug, platform = 'android') => api.get(`/api/app-updates/${encodeURIComponent(slug)}/manifest${qs({ platform })}`),
};

export const organizerTenant = {
  get: (tenant) => api.get(`/api/organizer/tenant${qs({ tenant })}`),
  update: (body, tenant) => api.put(`/api/organizer/tenant${qs({ tenant })}`, body),
  audience: (tenant) => api.get(`/api/organizer/audience${qs({ tenant })}`),
  exportAudience: (tenant) => downloadCsv(`/organizer/audience/export${qs({ tenant })}`, `${tenant || 'tenant'}-audience-${new Date().toISOString().slice(0, 10)}.csv`),
};

export const adminTenants = {
  list: (params) => api.get(`/api/admin/tenants${qs(params)}`),
  get: (slug) => api.get(`/api/admin/tenants/${encodeURIComponent(slug)}`),
  create: (body) => api.post('/api/admin/tenants', body),
  update: (slug, body) => api.patch(`/api/admin/tenants/${encodeURIComponent(slug)}`, body),
  activate: (slug) => api.post(`/api/admin/tenants/${encodeURIComponent(slug)}/activate`),
  suspend: (slug) => api.post(`/api/admin/tenants/${encodeURIComponent(slug)}/suspend`),
  addOrganizer: (slug, organizerId) => api.post(`/api/admin/tenants/${encodeURIComponent(slug)}/organizers`, { organizer_id: organizerId }),
  removeOrganizer: (slug, organizerId) => api.delete(`/api/admin/tenants/${encodeURIComponent(slug)}/organizers/${organizerId}`),
  addMember: (slug, userId, role) => api.post(`/api/admin/tenants/${encodeURIComponent(slug)}/members`, { user_id: userId, role }),
  removeMember: (slug, userId) => api.delete(`/api/admin/tenants/${encodeURIComponent(slug)}/members/${userId}`),
  pool: () => api.get('/api/admin/audience/pool'),
  exportPool: () => downloadCsv('/admin/audience/pool/export', `snt-pool-${new Date().toISOString().slice(0, 10)}.csv`),
};

export const customThemes = {
  list: () => api.get('/api/admin/custom-themes'),
  validate: (body) => api.post('/api/admin/custom-themes/validate', body),
  save: (id, body) => api.put(`/api/admin/custom-themes/${encodeURIComponent(id)}`, body),
  remove: (id) => api.delete(`/api/admin/custom-themes/${encodeURIComponent(id)}`),
  audit: () => api.get('/api/admin/settings/audit/custom_themes'),
};

export default { tenantPublic, organizerTenant, adminTenants, customThemes };
